import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight, Check } from "lucide-react";
import { capabilities as mockCapabilities } from "../mock";
import { getCapabilities } from "../api";
import { useI18n } from "../i18n/I18nContext";

const Capabilities = () => {
  const { t, localize } = useI18n();
  const [rawCapabilities, setCapabilities] = useState(mockCapabilities);

  useEffect(() => {
    getCapabilities()
      .then((data) => {
        if (Array.isArray(data) && data.length) setCapabilities(data);
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const capabilities = localize("capabilities", rawCapabilities);
  const approach = [
    { step: "01", title: t("step1Title"), text: t("step1Text") },
    { step: "02", title: t("step2Title"), text: t("step2Text") },
    { step: "03", title: t("step3Title"), text: t("step3Text") },
  ];
  const values = [t("value1"), t("value2"), t("value3"), t("value4")];

  return (
    <div>
      <section className="bg-[#0a2540] text-white py-20 md:py-28">
        <div className="max-w-[1400px] mx-auto px-5 md:px-8">
          <span className="text-sm font-700 uppercase tracking-widest text-[#5eb0f5]">
            {t("capabilities")}
          </span>
          <h1 className="font-display font-800 text-4xl md:text-6xl mt-3 max-w-3xl leading-[1.05]">
            {t("capsPageTitle")}
          </h1>
          <p className="mt-6 text-slate-300 text-lg max-w-2xl">
            {t("capsPageBody")}
          </p>
        </div>
      </section>

      <section className="max-w-[1400px] mx-auto px-5 md:px-8 py-20">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-slate-200 rounded-lg overflow-hidden border border-slate-200">
          {capabilities.map((c) => (
            <div key={c.id} className="group bg-white p-8 md:p-10 hover:bg-slate-50 transition-colors">
              <div className="flex items-start justify-between">
                <h3 className="font-display font-700 text-2xl text-[#0a2540] group-hover:text-[#0a6ed1] transition-colors">
                  {c.title}
                </h3>
                <ArrowUpRight className="w-6 h-6 text-slate-300 group-hover:text-[#0a6ed1] transition-colors" />
              </div>
              <p className="mt-4 text-slate-500 text-[15px] leading-relaxed">
                {c.description}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* Approach */}
      <section className="bg-slate-50 py-20 md:py-28">
        <div className="max-w-[1400px] mx-auto px-5 md:px-8">
          <div className="max-w-2xl mb-14">
            <span className="text-sm font-700 uppercase tracking-widest text-[#0a6ed1]">
              {t("howWeWork")}
            </span>
            <h2 className="font-display font-800 text-3xl md:text-5xl text-[#0a2540] mt-2">
              {t("approachTitle")}
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
            {approach.map((a) => (
              <div key={a.step} className="bg-white rounded-lg p-8 border border-slate-200">
                <div className="font-display font-800 text-5xl text-[#0a6ed1]/20">{a.step}</div>
                <h3 className="font-display font-700 text-xl text-[#0a2540] mt-4">{a.title}</h3>
                <p className="mt-3 text-slate-500 text-[15px] leading-relaxed">{a.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Value list */}
      <section className="max-w-[1400px] mx-auto px-5 md:px-8 py-20 md:py-28">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">
          <div>
            <h2 className="font-display font-800 text-3xl md:text-4xl text-[#0a2540] leading-tight">
              {t("whyLeaders")}
            </h2>
            <ul className="mt-8 space-y-4">
              {values.map((v) => (
                <li key={v} className="flex items-start gap-3">
                  <span className="flex items-center justify-center w-6 h-6 rounded-full bg-[#0a6ed1]/10 mt-0.5">
                    <Check className="w-4 h-4 text-[#0a6ed1]" />
                  </span>
                  <span className="text-slate-600 text-[17px]">{v}</span>
                </li>
              ))}
            </ul>
            <Link
              to="/contact"
              className="mt-8 inline-flex items-center gap-2 bg-[#0a2540] hover:bg-[#16355c] text-white font-600 px-7 py-3.5 rounded-full transition-colors"
            >
              {t("startConversation")} <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <img src="https://images.pexels.com/photos/7688106/pexels-photo-7688106.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940" alt="team" className="rounded-lg object-cover h-full w-full" />
            <img src="https://images.unsplash.com/photo-1714974528737-3e6c7e4d11af?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjV8MHwxfHNlYXJjaHwzfHxjb3Jwb3JhdGUlMjBjb25zdWx0aW5nfGVufDB8fHx8MTc4NzM4OTY3OHww&ixlib=rb-4.1.0&q=85" alt="meeting" className="rounded-lg object-cover h-full w-full mt-8" />
          </div>
        </div>
      </section>
    </div>
  );
};

export default Capabilities;
